import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/context/auth";
import Input from "@/components/input";
import { LanguageToggle } from "@/components/language-toggle";
import { notify } from "@/utils";
import { Logger } from "@/utils/logger";

import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WorkerConfirmDetailsStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const auth = useAuth();
  const { data, patch, saveDraft } = useWorkerOnboarding();

  React.useEffect(() => {
    if (data.email) return;
    const email = auth.user?.email ?? "";
    const name = [auth.user?.firstName, auth.user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (name || email) {
      patch({
        name: data.name || name,
        email: data.email || email,
      });
    }
  }, [auth.user, data.email, data.name, patch]);

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      if (!data.name.trim()) {
        notify.error(t("workerConfirmDetails.nameRequired"));
        return false;
      }
      if (!EMAIL_RE.test(data.email)) {
        notify.error(t("workerConfirmDetails.emailInvalid"));
        return false;
      }
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker onboarding save failed at confirm-details", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("workerConfirmDetails.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, data, saveDraft, t]);

  return (
    <section className="w-full flex flex-col gap-5">
      <h2 className="heading-1">{t("workerConfirmDetails.title")}</h2>
      <p className="body-small text-gray-600">
        {t("workerConfirmDetails.description")}
      </p>

      <section className="flex flex-col gap-3 mt-2">
        <h3 className="text-xl font-semibold text-garnet">
          {t("languagePreference.title")}
        </h3>
        <p className="text-sm text-garnet/80">
          {t("languagePreference.description")}
        </p>
        <LanguageToggle variant="card" persistToBackend />
      </section>

      <div className="border-garnet/25 w-full border my-2" />

      <div className="flex flex-col gap-4 max-w-[420px]">
        <Input
          label={t("workerConfirmDetails.fullNameLabel")}
          placeholder={t("workerConfirmDetails.fullNamePlaceholder")}
          value={data.name}
          onChange={(e) => patch({ name: e.currentTarget.value })}
        />
        <Input
          label={t("workerConfirmDetails.emailLabel")}
          value={data.email}
          disabled
          readOnly
        />
      </div>
    </section>
  );
}
